import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useIdentifies, useCreateIdentify, useUpdateIdentify, useDeleteIdentify } from "@/hooks/use-identifies";
import { useEndpoints } from "@/hooks/use-endpoints";
import type { Identify } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const columns: Column<Identify>[] = [
  { key: "name", header: "Name" },
  { key: "match", header: "Match" },
  { key: "endpoint_id", header: "Endpoint ID", render: (i) => i.endpoint_id ? <span className="font-mono text-xs">{i.endpoint_id.slice(0, 8)}...</span> : <span className="text-muted-foreground">none</span> },
  { key: "srv_lookups", header: "SRV", render: (i) => i.srv_lookups ? "Yes" : "No" },
];

export default function IdentifiesPage() {
  const { data, isLoading } = useIdentifies();
  const { data: endpoints } = useEndpoints();
  const createMut = useCreateIdentify();
  const updateMut = useUpdateIdentify();
  const deleteMut = useDeleteIdentify();

  const defaultIdentify = { name: "", endpoint_id: null as string | null, match: null as string | null, match_header: null as string | null, match_request_uri: null as string | null, srv_lookups: true };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Identify | null>(null);
  const [form, setForm] = useState(defaultIdentify);

  const openCreate = () => { setEditing(null); setForm(defaultIdentify); setOpen(true); };
  const openEdit = (i: Identify) => {
    setEditing(i);
    setForm({ name: i.name, endpoint_id: i.endpoint_id ?? null, match: i.match ?? null, match_header: i.match_header ?? null, match_request_uri: i.match_request_uri ?? null, srv_lookups: i.srv_lookups });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("Identify updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("Identify created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const handleDelete = (i: Identify) => {
    deleteMut.mutate(i.id, { onSuccess: () => toast.success("Identify deleted"), onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Identifies</h1>
          <p className="text-sm text-muted-foreground mt-1">PJSIP identify (IP-based endpoint matching)</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create Identify</Button>
      </div>

      <DataTable columns={columns} data={data} isLoading={isLoading} getRowKey={(i) => i.id} onEdit={openEdit} onDelete={handleDelete} emptyMessage="No identifies configured." />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit Identify" : "Create Identify"}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name * <HelpIcon text="Identify rule name. Example: match-trunk-a, match-office-phones." /></Label>
              <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="endpoint_id">Endpoint * <HelpIcon text="Which endpoint to use when this Identify rule matches. If unassigned, the rule is inactive." /></Label>
              <Select id="endpoint_id" value={form.endpoint_id ?? ""} onChange={(e) => updateField("endpoint_id", e.target.value || null)}>
                <option value="">Select endpoint</option>
                {endpoints?.map((ep) => <option key={ep.id} value={ep.id}>{ep.name}</option>)}
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="match">Match IP/Header <HelpIcon text="Source IP or CIDR range to match incoming SIP traffic against. Asterisk matches the first Identify rule whose match covers the source IP. Example: 203.0.113.50 (single IP), 192.168.1.0/24 (subnet), yourtrunk.pstn.twilio.com (hostname)." /></Label>
            <Input id="match" value={form.match ?? ""} onChange={(e) => updateField("match", e.target.value || null)} placeholder="192.168.0.0/16" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="match_header">Match Header <HelpIcon text="Match against a specific SIP header value. Format: Header-Name: value. Less common than IP matching." /></Label>
              <Input id="match_header" value={form.match_header ?? ""} onChange={(e) => updateField("match_header", e.target.value || null)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="match_request_uri">Match Request URI <HelpIcon text="Match against the Request-URI in the incoming INVITE. Useful for multi-tenant setups. Example: sip:tenant-a.domain.com." /></Label>
              <Input id="match_request_uri" value={form.match_request_uri ?? ""} onChange={(e) => updateField("match_request_uri", e.target.value || null)} />
            </div>
          </div>
          <Switch id="srv_lookups" checked={form.srv_lookups} onChange={(e) => updateField("srv_lookups", e.target.checked)} label={<span>SRV Lookups <HelpIcon text="Perform DNS SRV record lookups for the matched endpoint. Leave enabled for standard SIP routing. Disable for static IP-only deployments." /></span>} />

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
